import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { AdminLayout } from '../../components/admin/AdminLayout';
import { API_BASE } from '../../config/api';

export const LeadDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [lead, setLead] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [formConfig, setFormConfig] = useState(null); // For dynamic forms
  const [fieldRegistry, setFieldRegistry] = useState({}); // Field definitions

  const backendUrl = API_BASE;

  useEffect(() => {
    fetchLeadDetail();
  }, [id]);

  const fetchLeadDetail = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('admin_token');
      
      if (!token) {
        navigate('/admin/login');
        return;
      }

      const response = await fetch(`${backendUrl}/api/admin/forms/${id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.status === 401) {
        localStorage.removeItem('admin_token');
        navigate('/admin/login');
        return;
      }

      if (!response.ok) throw new Error('Lead not found');

      const data = await response.json();
      // API returns { success: true, form: {...} }
      const leadData = data.form || data;
      setLead(leadData);

      // If it's a dynamic form, fetch form config and fields
      if (leadData.form_type === 'dynamic' && leadData.form_slug) {
        await fetchDynamicFormConfig(leadData.form_slug, token);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchDynamicFormConfig = async (slug, token) => {
    try {
      // Fetch form definition
      const formResponse = await fetch(`${backendUrl}/api/public/forms/${slug}`);
      if (formResponse.ok) {
        const formData = await formResponse.json();
        setFormConfig(formData);

        // Build field registry map
        const fieldsMap = {};
        if (formData.fields) {
          formData.fields.forEach(field => {
            fieldsMap[field.key] = field;
          });
        }
        setFieldRegistry(fieldsMap);
      }
    } catch (err) {
      console.error('Error fetching dynamic form config:', err);
    }
  };

  const updateStatus = async (newStatus) => {
    try {
      setUpdating(true);
      const token = localStorage.getItem('admin_token');

      const response = await fetch(`${backendUrl}/api/admin/forms/${id}/status`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ lead_status: newStatus })
      });

      if (!response.ok) throw new Error('Failed to update status');

      setLead(prev => ({ ...prev, lead_status: newStatus, status: newStatus }));
    } catch (err) {
      console.error(err);
      alert('Failed to update status');
    } finally {
      setUpdating(false);
    }
  };

  const deleteLead = async () => {
    if (!window.confirm('Are you sure you want to delete this lead? This action cannot be undone.')) {
      return;
    }

    try {
      setDeleting(true);
      const token = localStorage.getItem('admin_token');

      const response = await fetch(`${backendUrl}/api/admin/forms/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) throw new Error('Failed to delete lead');

      navigate('/admin/leads');
    } catch (err) {
      console.error(err);
      alert('Failed to delete lead');
    } finally {
      setDeleting(false);
    }
  };

  const getFormTypeLabel = (type, slug) => {
    if (type === 'dynamic' && slug) {
      return `📋 ${slug}`;
    }
    const labels = {
      'general_inquiry': 'General Inquiry',
      'appointment_booking': 'Appointment Booking',
      'campus_tour': 'Campus Tour',
      'dynamic': 'Custom Form'
    };
    return labels[type] || type || 'Unknown';
  };

  const getStatusBadge = (status) => {
    const statusValue = status || 'new';
    const styles = {
      'new': 'bg-blue-50 text-blue-700 border border-blue-200',
      'contacted': 'bg-amber-50 text-amber-700 border border-amber-200',
      'closed': 'bg-emerald-50 text-emerald-700 border border-emerald-200'
    };
    return styles[statusValue] || 'bg-gray-50 text-gray-700 border border-gray-200';
  };

  const getCurrentStatus = () => {
    return lead?.lead_status || lead?.status || 'new';
  };

  // Helper to download file
  const downloadFile = async (fileId, fileName) => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${backendUrl}/api/admin/files/${fileId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) throw new Error('Download failed');

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName || 'download';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      console.error('Error downloading file:', err);
      alert('Failed to download file');
    }
  };

  // Helper to render dynamic form field
  const renderDynamicField = (fieldKey, value, fieldDef, files) => {
    if (!fieldDef) return null;

    // Skip if no value and no files
    if ((!value || value === '' || value === null || value === undefined) && (!files || files.length === 0)) {
      return null;
    }

    const getFieldIcon = (fieldType) => {
      const icons = {
        text: (
          <svg className="w-4 h-4 text-[#6B7280]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z" />
          </svg>
        ),
        textarea: (
          <svg className="w-4 h-4 text-[#6B7280]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h7" />
          </svg>
        ),
        email: (
          <svg className="w-4 h-4 text-[#6B7280]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
          </svg>
        ),
        phone: (
          <svg className="w-4 h-4 text-[#6B7280]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
          </svg>
        ),
        number: (
          <svg className="w-4 h-4 text-[#6B7280]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 20l4-16m2 16l4-16M6 9h14M4 15h14" />
          </svg>
        ),
        date: (
          <svg className="w-4 h-4 text-[#6B7280]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
        ),
        select: (
          <svg className="w-4 h-4 text-[#6B7280]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
          </svg>
        ),
        radio: (
          <svg className="w-4 h-4 text-[#6B7280]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <circle cx="12" cy="12" r="10" strokeWidth={2} />
            <circle cx="12" cy="12" r="4" fill="currentColor" />
          </svg>
        ),
        checkbox: (
          <svg className="w-4 h-4 text-[#6B7280]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        ),
        file: (
          <svg className="w-4 h-4 text-[#6B7280]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
          </svg>
        )
      };
      return icons[fieldType] || icons.text;
    };

    return (
      <div key={fieldKey} className="flex items-start gap-3 py-3 border-b border-[#F3F4F6] last:border-0">
        <div className="w-8 h-8 bg-[#F3F4F6] rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
          {getFieldIcon(fieldDef.field_type)}
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-xs font-medium text-[#6B7280] mb-0.5">{fieldDef.label}</p>
          
          {/* Render value based on field type */}
          {fieldDef.field_type === 'checkbox' && Array.isArray(value) ? (
            <div className="flex flex-wrap gap-2">
              {value.map((v, idx) => (
                <span key={idx} className="px-2 py-1 bg-[#E0E7FF] text-[#3730A3] text-xs rounded-lg font-medium">
                  {v}
                </span>
              ))}
            </div>
          ) : fieldDef.field_type === 'textarea' ? (
            <div className="bg-[#F9FAFB] rounded-lg p-3 text-sm text-[#374151] whitespace-pre-wrap">
              {value}
            </div>
          ) : fieldDef.field_type === 'file' && files && files.length > 0 ? (
            <div className="space-y-2">
              {files.map((fileId, idx) => (
                <button
                  key={fileId}
                  onClick={() => downloadFile(fileId, `${fieldDef.label}_${idx + 1}`)}
                  className="flex items-center gap-2 px-3 py-2 bg-[#F9FAFB] hover:bg-[#E5E7EB] rounded-lg text-sm text-[#1E40AF] hover:text-[#1e3a8a] transition-colors border border-[#E5E7EB]"
                  data-testid={`download-file-${fieldKey}-${idx}`}
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  Download File {files.length > 1 ? `${idx + 1}` : ''}
                </button>
              ))}
            </div>
          ) : value ? (
            <p className="text-sm text-[#111827] break-words">{value}</p>
          ) : null}
        </div>
      </div>
    );
  };

  // Helper to render field if it exists
  const renderField = (label, value, icon) => {
    if (!value || value === '' || value === null || value === undefined) return null;
    return (
      <div className="flex items-start gap-3 py-3 border-b border-[#F3F4F6] last:border-0">
        <div className="w-8 h-8 bg-[#F3F4F6] rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
          {icon}
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-xs font-medium text-[#6B7280] mb-0.5">{label}</p>
          <p className="text-sm text-[#111827] break-words">{value}</p>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <div className="inline-block w-12 h-12 border-4 border-[#1E40AF] border-t-transparent rounded-full animate-spin mb-4"></div>
            <p className="text-[#6B7280]">Loading lead details...</p>
          </div>
        </div>
      </AdminLayout>
    );
  }

  if (!lead) {
    return (
      <AdminLayout>
        <div className="text-center py-12">
          <svg className="w-16 h-16 mx-auto text-[#E5E7EB] mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
          <p className="text-[#6B7280] mb-4">Lead not found</p>
          <button
            onClick={() => navigate('/admin/leads')}
            className="px-6 py-3 bg-[#1E40AF] text-white rounded-lg font-medium"
          >
            Back to Leads
          </button>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6 max-w-4xl">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
          <div>
            <button
              onClick={() => navigate('/admin/leads')}
              className="text-[#1E40AF] hover:text-[#1e3a8a] font-medium text-sm flex items-center gap-2 mb-3"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              Back to Leads
            </button>
            <h1 className="text-2xl font-bold text-[#111827]" data-testid="lead-name">
              {lead.form_type === 'dynamic' ? (formConfig?.title || lead.form_slug || 'Lead Details') : (lead.full_name || 'Lead Details')}
            </h1>
            <p className="text-[#6B7280] text-sm mt-1">{getFormTypeLabel(lead.form_type, lead.form_slug)}</p>
          </div>
          <div className="flex items-center gap-3">
            <span className={`px-3 py-1.5 rounded-lg text-sm font-semibold ${getStatusBadge(getCurrentStatus())}`}>
              {getCurrentStatus().charAt(0).toUpperCase() + getCurrentStatus().slice(1)}
            </span>
            <button
              onClick={deleteLead}
              disabled={deleting}
              className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50"
              title="Delete Lead"
              data-testid="delete-lead"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
            </button>
          </div>
        </div>

        {/* Status Update */}
        <div className="bg-white rounded-xl border border-[#E5E7EB] p-5">
          <h2 className="text-sm font-semibold text-[#6B7280] uppercase tracking-wide mb-4">Update Status</h2>
          <div className="flex flex-wrap gap-2">
            {['new', 'contacted', 'closed'].map(status => (
              <button
                key={status}
                onClick={() => updateStatus(status)}
                disabled={updating || getCurrentStatus() === status}
                className={`px-5 py-2.5 rounded-lg font-medium text-sm transition-all disabled:opacity-50 ${
                  getCurrentStatus() === status
                    ? 'bg-[#1E40AF] text-white'
                    : 'bg-[#F9FAFB] text-[#374151] border border-[#E5E7EB] hover:border-[#1E40AF] hover:text-[#1E40AF]'
                }`}
                data-testid={`status-${status}`}
              >
                {status.charAt(0).toUpperCase() + status.slice(1)}
              </button>
            ))}
          </div>
        </div>

        {/* Dynamic Form Submission Data */}
        {lead.form_type === 'dynamic' && lead.data && (
          <div className="bg-white rounded-xl border border-[#E5E7EB] p-5">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-sm font-semibold text-[#6B7280] uppercase tracking-wide">Form Submission Data</h2>
              {formConfig?.ux_mode === 'guided' && (
                <span className="text-xs text-[#6B7280] bg-[#F3F4F6] px-2 py-1 rounded">
                  Guided Form
                </span>
              )}
            </div>
            {formConfig?.description && (
              <p className="text-sm text-[#6B7280] mb-4 pb-4 border-b border-[#F3F4F6]">
                {formConfig.description}
              </p>
            )}
            <div className="space-y-1">
              {formConfig && formConfig.fields ? (
                // Sort fields by order and render with labels
                formConfig.fields
                  .sort((a, b) => a.order - b.order)
                  .map(field => {
                    const value = lead.data[field.key];
                    const files = lead.files?.[field.key];
                    return renderDynamicField(field.key, value, field, files);
                  })
              ) : (
                // Fallback: Show raw data if form config not available
                Object.entries(lead.data).map(([key, value]) => (
                  <div key={key} className="flex items-start gap-3 py-3 border-b border-[#F3F4F6] last:border-0">
                    <div className="w-8 h-8 bg-[#F3F4F6] rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
                      <svg className="w-4 h-4 text-[#6B7280]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-medium text-[#6B7280] mb-0.5">{key}</p>
                      <p className="text-sm text-[#111827] break-words">
                        {Array.isArray(value) ? value.join(', ') : String(value)}
                      </p>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        )}

        {/* Contact Information */}
        {lead.form_type !== 'dynamic' && (
          <div className="bg-white rounded-xl border border-[#E5E7EB] p-5">
          <h2 className="text-sm font-semibold text-[#6B7280] uppercase tracking-wide mb-4">Contact Information</h2>
          <div className="space-y-1">
            {renderField('Full Name', lead.full_name, (
              <svg className="w-4 h-4 text-[#6B7280]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
            ))}
            {renderField('Email', lead.email, (
              <svg className="w-4 h-4 text-[#6B7280]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
            ))}
            {renderField('Contact Number', lead.contact_number, (
              <svg className="w-4 h-4 text-[#6B7280]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
              </svg>
            ))}
            {renderField('WhatsApp Number', lead.whatsapp_number, (
              <svg className="w-4 h-4 text-[#6B7280]" fill="currentColor" viewBox="0 0 24 24">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.890-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
              </svg>
            ))}
            {renderField('Country', lead.country, (
              <svg className="w-4 h-4 text-[#6B7280]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            ))}
            {renderField('Nationality', lead.nationality, (
              <svg className="w-4 h-4 text-[#6B7280]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 21v-4m0 0V5a2 2 0 012-2h6.5l1 1H21l-3 6 3 6h-8.5l-1-1H5a2 2 0 00-2 2zm9-13.5V9" />
              </svg>
            ))}
          </div>
        </div>
        )}

        {/* Interest & Preferences */}
        {lead.form_type !== 'dynamic' && (
        <div className="bg-white rounded-xl border border-[#E5E7EB] p-5">
          <h2 className="text-sm font-semibold text-[#6B7280] uppercase tracking-wide mb-4">Interest & Preferences</h2>
          <div className="space-y-1">
            {renderField('Interested Course', lead.interested_course, (
              <svg className="w-4 h-4 text-[#6B7280]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
              </svg>
            ))}
            {renderField('Interested Program', lead.interested_program, (
              <svg className="w-4 h-4 text-[#6B7280]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
              </svg>
            ))}
            {renderField('Interested University', lead.interested_university, (
              <svg className="w-4 h-4 text-[#6B7280]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 14v3m4-3v3m4-3v3M3 21h18M3 10h18M3 7l9-4 9 4M4 10h16v11H4V10z" />
              </svg>
            ))}
            {renderField('Level of Study', lead.level_of_study, (
              <svg className="w-4 h-4 text-[#6B7280]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
              </svg>
            ))}
            {renderField('Field of Study', lead.field_of_study, (
              <svg className="w-4 h-4 text-[#6B7280]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
              </svg>
            ))}
            {renderField('Preferred Intake', lead.preferred_intake, (
              <svg className="w-4 h-4 text-[#6B7280]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
            ))}
            {renderField('Budget Range', lead.budget_range, (
              <svg className="w-4 h-4 text-[#6B7280]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            ))}
            {renderField('Current Qualification', lead.current_qualification, (
              <svg className="w-4 h-4 text-[#6B7280]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            ))}
            {renderField('English Proficiency', lead.english_proficiency, (
              <svg className="w-4 h-4 text-[#6B7280]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5h12M9 3v2m1.048 9.5A18.022 18.022 0 016.412 9m6.088 9h7M11 21l5-10 5 10M12.751 5C11.783 10.77 8.07 15.61 3 18.129" />
              </svg>
            ))}
          </div>
        </div>
        )}

        {/* Appointment / Tour Details */}
        {lead.form_type !== 'dynamic' && (lead.preferred_date || lead.preferred_time || lead.tour_date || lead.tour_time || lead.number_of_visitors) && (
          <div className="bg-white rounded-xl border border-[#E5E7EB] p-5">
            <h2 className="text-sm font-semibold text-[#6B7280] uppercase tracking-wide mb-4">Appointment / Tour Details</h2>
            <div className="space-y-1">
              {renderField('Preferred Date', lead.preferred_date ? new Date(lead.preferred_date).toLocaleDateString() : null, (
                <svg className="w-4 h-4 text-[#6B7280]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
              ))}
              {renderField('Preferred Time', lead.preferred_time, (
                <svg className="w-4 h-4 text-[#6B7280]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              ))}
              {renderField('Tour Date', lead.tour_date ? new Date(lead.tour_date).toLocaleDateString() : null, (
                <svg className="w-4 h-4 text-[#6B7280]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
              ))}
              {renderField('Tour Time', lead.tour_time, (
                <svg className="w-4 h-4 text-[#6B7280]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              ))}
              {renderField('Number of Visitors', lead.number_of_visitors, (
                <svg className="w-4 h-4 text-[#6B7280]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
              ))}
            </div>
          </div>
        )}

        {/* Message / Notes */}
        {lead.form_type !== 'dynamic' && (lead.message || lead.special_requests || lead.additional_notes) && (
          <div className="bg-white rounded-xl border border-[#E5E7EB] p-5">
            <h2 className="text-sm font-semibold text-[#6B7280] uppercase tracking-wide mb-4">Message & Notes</h2>
            {lead.message && (
              <div className="mb-4">
                <p className="text-xs font-medium text-[#6B7280] mb-2">Message</p>
                <div className="bg-[#F9FAFB] rounded-lg p-4 text-sm text-[#374151] whitespace-pre-wrap">
                  {lead.message}
                </div>
              </div>
            )}
            {lead.special_requests && (
              <div className="mb-4">
                <p className="text-xs font-medium text-[#6B7280] mb-2">Special Requests</p>
                <div className="bg-[#F9FAFB] rounded-lg p-4 text-sm text-[#374151] whitespace-pre-wrap">
                  {lead.special_requests}
                </div>
              </div>
            )}
            {lead.additional_notes && (
              <div>
                <p className="text-xs font-medium text-[#6B7280] mb-2">Additional Notes</p>
                <div className="bg-[#F9FAFB] rounded-lg p-4 text-sm text-[#374151] whitespace-pre-wrap">
                  {lead.additional_notes}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Metadata */}
        <div className="bg-white rounded-xl border border-[#E5E7EB] p-5">
          <h2 className="text-sm font-semibold text-[#6B7280] uppercase tracking-wide mb-4">Submission Info</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <p className="text-xs font-medium text-[#6B7280] mb-1">Submission ID</p>
              <p className="text-sm text-[#111827] font-mono">{lead.id}</p>
            </div>
            <div>
              <p className="text-xs font-medium text-[#6B7280] mb-1">Form Type</p>
              <p className="text-sm text-[#111827]">{getFormTypeLabel(lead.form_type, lead.form_slug)}</p>
            </div>
            {lead.form_slug && (
              <div>
                <p className="text-xs font-medium text-[#6B7280] mb-1">Form Slug</p>
                <p className="text-sm text-[#111827] font-mono">{lead.form_slug}</p>
              </div>
            )}
            <div>
              <p className="text-xs font-medium text-[#6B7280] mb-1">Submitted</p>
              <p className="text-sm text-[#111827]">{new Date(lead.created_at).toLocaleString()}</p>
            </div>
            {lead.updated_at && (
              <div>
                <p className="text-xs font-medium text-[#6B7280] mb-1">Last Updated</p>
                <p className="text-sm text-[#111827]">{new Date(lead.updated_at).toLocaleString()}</p>
              </div>
            )}
            {lead.source_page && (
              <div>
                <p className="text-xs font-medium text-[#6B7280] mb-1">Source Page</p>
                <p className="text-sm text-[#111827] break-all">{lead.source_page}</p>
              </div>
            )}
            {lead.source && (
              <div>
                <p className="text-xs font-medium text-[#6B7280] mb-1">Source</p>
                <p className="text-sm text-[#111827]">{lead.source}</p>
              </div>
            )}
            {lead.referral_code && (
              <div>
                <p className="text-xs font-medium text-[#6B7280] mb-1">Referral Code</p>
                <p className="text-sm text-[#111827]">{lead.referral_code}</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default LeadDetail;
